import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import { Bell } from "lucide-react";
import { recordNotificationView } from "../apis/notifications/post.record.view";
import {
  notificationDrawerStore,
  notificationStatusStore,
} from "../hooks/store";
import { CurrentAccountInfoContext } from "../routes/AuthRoutesProvider";
import { appBadgeManager } from "../utils/appBadge";
import { useNavigation } from "../hooks/useNavigation";
import NotificationDrawer from "./NotificationDrawer";

interface NotificationButtonProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  badgeClassName?: string;
  strokeWidth?: number;
  showLabel?: boolean;
  label?: string;
  labelClassName?: string;
  isMobile?: boolean;
  onNavigationEvent?: (eventData: { action: 'opened' | 'closed'; from: string }) => void;
}

const NotificationButton: React.FC<NotificationButtonProps> = ({
  size = "md",
  className = "",
  buttonClassName = "",
  iconClassName = "",
  badgeClassName = "",
  strokeWidth = 1.5,
  showLabel = false,
  label = "Notifications",
  labelClassName = "",
  isMobile = false,
  onNavigationEvent,
}) => {
  const { data: notificationStatus } = useSnapshot(notificationStatusStore);
  const { isOpen: isDrawerOpen } = useSnapshot(notificationDrawerStore);
  const { user } = useContext(CurrentAccountInfoContext);
  const { id } = useParams<{ id: string }>();
  const weddingId = id || user.weddingID;
  const { openNotificationDrawer, closeNotificationDrawer } = useNavigation();

  const handleDrawerOpen = () => {
    openNotificationDrawer();
    onNavigationEvent?.({ action: 'opened', from: 'notification-button' });
  };

  const handleDrawerClose = () => {
    closeNotificationDrawer();
    onNavigationEvent?.({ action: 'closed', from: 'notification-button' });
  };

  // Handle drawer opening via websocket (clear badge and record view)
  useEffect(() => {
    if (isDrawerOpen) {
      // Clear notification badge if there are unread notifications
      if (notificationStatus.unreadCount > 0) {
        notificationStatusStore.data = {
          unreadCount: 0,
          isLoading: false,
          error: null,
        };

        // Clear app badge
        appBadgeManager.clearBadge();

        // Record the view on server
        recordNotificationView({
          onCompleted: () => {
            // View recorded successfully
          },
          onError: (err) => {
            console.error("Failed to record notification view:", err);
          },
        });
      }
    }
  }, [isDrawerOpen, notificationStatus.unreadCount]);


  // Size classes
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };

  const iconSizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          onClick={handleDrawerOpen}
          className={`
            ${showLabel ? 'flex flex-col items-center justify-center' : `${sizeClasses[size]} flex items-center justify-center rounded-md`}
            text-gray-600 hover:text-gray-900
            hover:bg-gray-100
            transition-colors duration-200
            focus:outline-none
            ${buttonClassName}
          `}
          aria-label="Open notifications"
        >
          <div className="relative">
            {/* Bell Icon from Lucide React */}
            <Bell
              className={`${iconSizeClasses[size]} ${iconClassName}`}
              strokeWidth={strokeWidth}
            />

            {notificationStatus.unreadCount > 0 && (
              <div className={`absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-medium rounded-full min-w-[16px] h-[16px] flex items-center justify-center ${badgeClassName}`}>
                {notificationStatus.unreadCount > 9
                  ? "9+"
                  : notificationStatus.unreadCount}
              </div>
            )}
          </div>

          {showLabel && (
            <span className={`text-xs mt-1 ${labelClassName}`}>
              {label}
            </span>
          )}
        </button>
      </div>

      <NotificationDrawer isOpen={isDrawerOpen} onClose={handleDrawerClose} isMobile={isMobile} />
    </>
  );
};

export default NotificationButton;
